import { NavigationGuardNext, RouteLocationNormalized } from 'vue-router';
import Swal from 'sweetalert2';

declare const DEV_BYPASS_AUTH: string | undefined;

export const isDevBypass = typeof DEV_BYPASS_AUTH !== 'undefined' && DEV_BYPASS_AUTH === 'true';

export interface Auth0User {
        app_metadata?: {
                modules?: string[];
        };
        [key: string]: unknown;
}

export const routeToModuleMap: Record<string, string> = {
        '/model2d': '2D Models',
        '/model3d': '3D Models',
        '/badges': 'Badges',
        '/crosssell': 'Cross Sell',
        '/upsell': 'Upsells',
        '/flexgroup': 'Flex Group',
        '/listeritem': 'Lister Items',
        '/offerings': 'Offering',
        '/offeringoption': 'Offering Option',
        '/pdp': 'PDPs',
        '/pricing': 'Pricing',
        '/shipping': 'Shipping',
        '/sticker': 'Stickers',
        '/appsetting': 'App Setting',
        '/application': 'API permission',
        '/region': 'Regions',
        '/currency': 'Currencies',
        '/country': 'Countries',
        '/hyperlink': 'Hyperlinks',
        '/photoframe': 'Photoframes',
        '/layout': 'Layouts',
        '/themecategory': 'Category',
        '/themes': 'Themes',
        '/template': 'Templates',
        '/pagebg': 'Page Background',
        '/discount-bulk': 'Bulk Discount',
        '/discount': 'Discount',
        '/discount-voucher': 'Discount Voucher',
        '/shop-integration': 'Shop Integrations',
};

// Legacy module names still accepted for a module, so users whose Auth0
// app_metadata has not been updated yet do not lose access after a rename.
// (All migrations complete — no active aliases remain.)
export const legacyModuleAliases: Record<string, string[]> = {};

export function getRequiredModule(path: string): string | null {
        for (const [routePrefix, moduleName] of Object.entries(routeToModuleMap)) {
                if (path === routePrefix || path.startsWith(routePrefix + '/')) {
                        return moduleName;
                }
        }
        return null;
}

export function getUserAllowedModules(user: Auth0User | undefined): string[] {
        if (isDevBypass) return [];
        return user?.app_metadata?.modules || [];
}

export function canUserAccessModule(user: Auth0User | undefined, moduleName: string): boolean {
        if (isDevBypass) return true;
        if (!moduleName) return true;
        const allowedModules = getUserAllowedModules(user);
        if (allowedModules.length === 0) return true;
        if (allowedModules.includes(moduleName)) return true;
        const aliases = legacyModuleAliases[moduleName] || [];
        return aliases.some((alias) => allowedModules.includes(alias));
}

export function hasUserFullAccess(user: Auth0User | undefined): boolean {
        if (isDevBypass) return true;
        const allowedModules = getUserAllowedModules(user);
        return allowedModules.length === 0;
}

export function createModuleGuard(getUser: () => Auth0User | undefined) {
        return (
                to: RouteLocationNormalized,
                from: RouteLocationNormalized,
                next: NavigationGuardNext,
        ) => {
                if (isDevBypass) {
                        next();
                        return;
                }

                const requiredModule = getRequiredModule(to.path);

                if (!requiredModule) {
                        next();
                        return;
                }

                const user = getUser();

                if (canUserAccessModule(user, requiredModule)) {
                        next();
                } else {
                        Swal.fire({
                                title: 'Access Denied',
                                text: `You don't have permission to access the ${requiredModule} module.`,
                                icon: 'error',
                                confirmButtonColor: '#FF6600',
                        });
                        if (from.path === '/') {
                                next('/dashboard');
                        } else {
                                next(false);
                        }
                }
        };
}
